import {
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from "@mui/material";
import Icon from "components/icon";
import CustomTextField from "../../components/mui/text-field";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UserLayout from "components/layouts/UserLayout";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const Reports = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const reportCategories = [
    {
      title: "Customer Reports",
      reports: ["Customer Jobs Report", "Lead Conversion Report"],
      accessible: false, 
    },
    {
      title: "Employee Reports",
      reports: [
        "Employee Productivity Report",
        "Sales by Employee",
       { name: "Login Activity Report", path: "/reports/loginactivity" },
        { name: "Time Clock Report", path: "/reports/timeclock" },
        { name: "Payroll Report", path: "/reports/payroll" },
        "Commission",
      ],
      accessible: true, 
    },
    {
      title: "Work Order Reports",
      reports: ["Work Order - All Jobs Report", "Request Conversion Report"],
      accessible: false, 
    },
    {
      title: "Finance Reports",
      reports: [
        "Invoice Report",
        "Discount Report - Invoice Level",
        "Discount Report - By Employee",
        "Payment Report",
        { name: "Sales Tax Report", path: "/reports/salestax" },
      ],
      accessible: true, 
    },
    { 
      title: "Service and Bundle Reports",
      reports: [
        { name: "Service and Bundle Reports", path: "/reports/serviceandbundles" }
      ],
      accessible: true,
    },
    {
      title: "Inventory Reports",
      reports: ["View Material and Assets", "Inventory Cost and Profit Report", "Service and Bundle Reports"],
      accessible: false, 
    },
    {
      title: "Other Reports",
      reports: [{ name:"Tags", path: "/reports/tags" }, "Customer Referral", "Marketing Source"],
      accessible: true, 
    },
  ];

  // Filter categories based on search
  const filteredCategories = reportCategories
    .map((category) => {
      const filteredReports = category.reports.filter((report) => {
        const reportName = report.name || report;
        return reportName.toLowerCase().includes(searchTerm.toLowerCase());
      });
      return { ...category, reports: filteredReports };
    })
    .filter((category) => category.reports.length > 0);
  const handleNavigation = (to) => {
    navigate(to);
  };

  const handlePageBack = () => {
    handleNavigation('/reports');    
  };

  return (
    <UserLayout 
      contentHeightFixed={false}
      NavURL={handlePageBack}
      activeLink="All Reports"
    >
      <Card
        sx={{
          p: 3,
          boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          height: "100%",
          width: "100%",
          overflowX: "auto",
          mx: "auto",
          mt: 4,
        }}
      >
        <CardContent sx={{ height: "100%", minWidth: { xs: "320px", sm: "100%" } }}>
          {/* Header */}
          <Typography variant="h3" gutterBottom>
            All Reports
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            View and access reports from every module in one place. Expand categories to see more reports, and tap a report to open its detailed view.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Search Bar */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", mb: 2 }}>
            <CustomTextField
              placeholder="Search"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="tabler:search" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Accordions */}
          {filteredCategories.map((category, i) => (
            <Accordion 
              key={i} 
              disableGutters 
              sx={{ 
                mb: 4, 
                borderRadius: 2, 
                overflow: "hidden",
                // Remove disabled styling
                '&.Mui-disabled': {
                  backgroundColor: 'transparent',
                  color: 'inherit'
                }
              }}
              disabled={!category.accessible} // Disable expansion but remove visual effects
            >
              <AccordionSummary 
                expandIcon={category.accessible ? <ExpandMoreIcon /> : null}
                sx={{
                  // Remove disabled styling from summary
                  '&.Mui-disabled': {
                    opacity: 1,
                    color: 'inherit',
                    cursor: 'default'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="h5">{category.title}</Typography>
                  {!category.accessible && (
                   <Chip 
                    label="Coming Soon" 
                    size="small"  
                     variant="filled"
                     sx={{ 
                   ml: 2,
                   backgroundColor: 'grey.300', 
                   color: 'grey.600', 
                   '& .MuiChip-icon': {
                   color: 'grey.600', 
                   }
                  }}
                  icon={<Icon icon="tabler:info-circle" />} 
                  />
                  )}
                </Box>
              </AccordionSummary>
              
              {category.accessible && (
                <AccordionDetails>
                  <List disablePadding>
                    {category.reports.map((report, j) => (
                      <ListItemButton
                        key={j}
                        sx={{ pl: 2 }}
                        onClick={() => report.path && navigate(report.path)}
                      >
                        <ListItemText primary={report.name || report} />
                      </ListItemButton>
                    ))}
                  </List>
                </AccordionDetails>
              )}
            </Accordion>
          ))}
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default Reports;